import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Leaf, Leaf as LeafIcon, ShieldCheck, HandHeart, ArrowRight, Star, Award,
} from "lucide-react";
import { BUSINESS } from "@/data/business";
import { getServiceIcon } from "@/lib/serviceIcons";
import { BookButton } from "@/components/site/BookButton";
import { Reveal } from "@/components/site/Reveal";
import { ReviewsSection } from "@/components/site/ReviewsSection";
import { ParticleField } from "@/components/site/ParticleField";
import { HeroVideo } from "@/components/site/HeroVideo";
import { Parallax } from "@/components/site/Parallax";
import { Seo } from "@/components/site/Seo";

const HERO_WORDS = ["Radiant,", "healthy", "skin,"];

const MARQUEE_LABELS = [
  "Classic Facial",
  "Anti-Aging Facial",
  "HydraFacial",
  "Dermaplaning",
  "LED Light Therapy",
  "Microcurrent Facial",
  "Derma Pen Treatment",
  "Chemical Exfoliation",
  "Microdermabrasion",
  "Brightening Facial",
  "Bridal Facial",
  "Organic Facial",
  "Eyebrow Shaping",
  "Tinting",
];

const HIGHLIGHTS = [
  { icon: Sparkles, title: "Personalized Care", desc: "Every treatment is tailored to your unique skin and goals." },
  { icon: Leaf, title: "Organic Approach", desc: "The highest-quality, natural products for optimal results." },
  { icon: ShieldCheck, title: "Proven Results", desc: "A decade of experience helping skin look its healthiest." },
];

/** Scroll-linked hero: video slowly zooms, content drifts up and fades. */
const useHeroScroll = (videoWrapRef, heroContentRef) => {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let raf = null;
    const update = () => {
      raf = null;
      const y = Math.min(window.scrollY, 900);
      if (videoWrapRef.current) {
        videoWrapRef.current.style.transform = `scale(${(1 + y * 0.00016).toFixed(4)}) translateY(${(y * 0.12).toFixed(1)}px)`;
      }
      if (heroContentRef.current) {
        heroContentRef.current.style.transform = `translateY(${(-y * 0.16).toFixed(1)}px)`;
        heroContentRef.current.style.opacity = String(Math.max(1 - y / 620, 0));
      }
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [videoWrapRef, heroContentRef]);
};

const HeroHeadline = () => (
  <h1 className="ts-hero mt-7 max-w-4xl text-4xl font-light leading-[1.08] tracking-tight text-[#F6F1E8] sm:text-6xl lg:text-7xl">
    {HERO_WORDS.map((word, i) => (
      <span key={word} className="word-mask">
        <span className="word-rise" style={{ "--wd": `${0.1 + i * 0.1}s` }}>
          {word}
          {"\u00A0"}
        </span>
      </span>
    ))}
    <br />
    <span className="word-mask">
      <span className="word-rise text-gradient-light text-shimmer font-serif italic" style={{ "--wd": "0.48s" }}>
        beautifully personalized
      </span>
    </span>
    <span className="word-mask">
      <span className="word-rise text-[color:var(--brand-amber)]" style={{ "--wd": "0.62s" }}>.</span>
    </span>
  </h1>
);

const HeroTrustRow = () => (
  <div className="ts-hero mt-10 flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-sm text-[rgba(247,241,232,0.92)]">
    <span className="inline-flex items-center gap-2"><ShieldCheck size={16} strokeWidth={1.75} /> Licensed &amp; experienced</span>
    <span className="inline-flex items-center gap-2"><LeafIcon size={16} strokeWidth={1.75} /> Organic &amp; natural</span>
    <span className="inline-flex items-center gap-2"><HandHeart size={16} strokeWidth={1.75} /> Women-owned</span>
    <span className="inline-flex items-center gap-2"><Star size={16} strokeWidth={1.75} className="fill-[#C89A4A] text-[#C89A4A]" /> 10+ years of trusted care</span>
  </div>
);

const HeroAwardBadge = () => (
  <a
    href={BUSINESS.googleReviewsUrl}
    target="_blank"
    rel="noreferrer"
    data-testid="hero-award-badge"
    className="glass glass-hover float-slow mt-9 inline-flex items-center gap-3.5 px-6 py-3.5 text-left"
  >
    <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#EFDDB9] to-[#C89A4A]">
      <Award size={20} strokeWidth={1.6} className="text-[#3A2C22]" />
    </span>
    <span>
      <span className="block text-sm font-semibold leading-tight">{BUSINESS.award.title}</span>
      <span className="block text-xs leading-tight text-[color:var(--text-subtle)]">{BUSINESS.award.subtitle}</span>
    </span>
  </a>
);

const MarqueeStrip = () => (
  <>
    <div className="keyline-glow" aria-hidden="true" />
    <div className="marquee relative border-b border-[color:rgba(43,31,23,0.08)] bg-[#FFFCF6]/40 py-4 backdrop-blur-sm" aria-hidden="true">
      <div className="marquee-track">
        {[0, 1].map((dup) => (
          <div key={dup} className="flex shrink-0 items-center">
            {MARQUEE_LABELS.map((label) => (
              <span key={`${dup}-${label}`} className="mx-6 inline-flex items-center gap-3 whitespace-nowrap font-serif text-lg italic text-[color:var(--text-subtle)]">
                {label}
                <Sparkles size={13} strokeWidth={1.5} className="text-[#C89A4A]" />
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  </>
);

const HeroSection = ({ videoWrapRef, heroContentRef }) => (
  <section data-testid="home-hero" className="noise relative overflow-hidden">
    {/* Looping candlelit video background (scroll-linked zoom) */}
    <div ref={videoWrapRef} className="absolute inset-0" style={{ willChange: "transform" }}>
      <HeroVideo />
    </div>
    <div className="video-tint" aria-hidden="true" />
    <div className="hero-scrim" aria-hidden="true" />
    <div className="rays" style={{ opacity: 0.22 }} aria-hidden="true" />
    <div className="mist top-[10%] left-[-25%]" style={{ opacity: 0.28 }} aria-hidden="true" />
    <div className="mist bottom-[14%] left-[-15%]" style={{ opacity: 0.22, animationDelay: "-8s", animationDuration: "20s" }} aria-hidden="true" />
    <ParticleField count={56} />
    <div className="video-fade-bottom" aria-hidden="true" />

    {/* Centered hero content */}
    <div className="container-x relative flex min-h-[76vh] flex-col items-center justify-center pb-24 pt-16 text-center sm:pt-24 lg:pb-28 lg:pt-24">
      <div ref={heroContentRef} className="fade-up flex flex-col items-center" style={{ willChange: "transform, opacity" }}>
        <span className="eyebrow-light">Licensed Skincare Specialist · Niles, IL</span>
        <HeroHeadline />
        <p className="ts-hero mt-7 max-w-xl text-base leading-relaxed text-[rgba(247,241,232,0.92)] sm:text-lg">
          A calm, candlelit day spa offering facials, HydraFacial, dermaplaning, advanced skin
          treatments, brows and tinting, all crafted around your skin with an organic, natural approach.
        </p>
        <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <BookButton testId="home-hero-book-appointment-button" className="px-8 py-3.5" />
          <Link
            to="/services"
            data-testid="home-hero-services-button"
            className="group btn-glass-light sheen inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold transition-[background-color,box-shadow,transform] duration-300 active:scale-[0.98]"
          >
            View Services <ArrowRight size={15} strokeWidth={1.75} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
        <HeroTrustRow />
        <HeroAwardBadge />
      </div>
    </div>

    <MarqueeStrip />
  </section>
);

const HighlightsSection = () => (
  <section data-testid="home-highlights" className="container-x pb-4 pt-4">
    <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-3">
      {HIGHLIGHTS.map((h, i) => (
        <Reveal key={h.title} delay={i * 0.06}>
          <div className="glass glass-hover flex h-full items-start gap-4 p-6">
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] bg-[color:rgba(217,183,122,0.34)]">
              <h.icon size={20} strokeWidth={1.6} className="text-[color:var(--text)]" />
            </span>
            <div>
              <h3 className="text-lg font-medium">{h.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-[color:var(--text-muted)]">{h.desc}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const AboutTeaser = () => (
  <section data-testid="home-about-teaser" className="section-pad">
    <div className="container-x grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
      <Reveal>
        <Parallax speed={0.1}>
          <div className="glass-strong overflow-hidden p-2 sm:p-3">
            <img
              src="/images/about-me.webp"
              alt="Bahra, licensed skincare specialist and owner of Bahra Skincare"
              className="h-[440px] w-full rounded-[14px] object-cover object-top sm:h-[560px]"
              loading="lazy"
            />
          </div>
        </Parallax>
      </Reveal>
      <Reveal delay={0.08}>
        <p className="eyebrow">About Bahra</p>
        <h2 className="mt-3 text-3xl font-light tracking-tight sm:text-4xl">A decade of dedicated skincare</h2>
        <p className="mt-5 text-base leading-relaxed text-[color:var(--text-muted)]">{BUSINESS.about.bio}</p>
        <Link to="/about" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold link-underline">
          Learn more about me <ArrowRight size={15} strokeWidth={1.75} />
        </Link>
      </Reveal>
    </div>
  </section>
);

const ServicesPreview = () => (
  <section data-testid="home-services-preview" className="section-pad bg-[color:rgba(217,183,122,0.13)]">
    <div className="container-x">
      <Reveal className="mx-auto max-w-2xl text-center">
        <p className="eyebrow">What We Offer</p>
        <h2 className="mt-3 text-3xl font-light tracking-tight sm:text-4xl lg:text-5xl">A full suite of day spa services</h2>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
        {BUSINESS.services.map((s, i) => {
          const Icon = getServiceIcon(s.icon);
          return (
            <Reveal key={s.name} delay={i * 0.05}>
              <div className="glass glass-hover h-full p-6">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-[12px] bg-[color:rgba(185,163,143,0.22)]">
                  <Icon size={20} strokeWidth={1.6} className="text-[color:var(--text)]" />
                </span>
                <h3 className="mt-4 text-lg font-medium">{s.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[color:var(--text-muted)]">{s.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
      <div className="mt-10 flex justify-center">
        <Link
          to="/services"
          className="group btn-glass-light sheen inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-[background-color,box-shadow,transform] duration-300 active:scale-[0.98]"
        >
          Explore all services <ArrowRight size={15} strokeWidth={1.75} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  </section>
);

const GalleryPreview = () => (
  <section data-testid="home-gallery-preview" className="section-pad">
    <div className="container-x">
      <Reveal className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="eyebrow">Real Results</p>
          <h2 className="mt-3 text-3xl font-light tracking-tight sm:text-4xl">Before &amp; after</h2>
        </div>
        <Link to="/gallery" className="inline-flex items-center gap-2 text-sm font-semibold link-underline">
          View full gallery <ArrowRight size={15} strokeWidth={1.75} />
        </Link>
      </Reveal>
      <div className="mt-10 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {BUSINESS.gallery.slice(0, 4).map((g, i) => (
          <Reveal key={g.src} delay={i * 0.05}>
            <div className="img-zoom overflow-hidden rounded-[18px] border border-[color:rgba(43,31,23,0.1)] bg-[color:var(--surface)] glass-hover">
              <img src={g.src} alt={g.alt} className="aspect-[4/5] w-full object-cover" loading="lazy" />
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CtaStrip = () => (
  <section className="section-pad">
    <div className="container-x">
      <Reveal>
        <div className="cta-dark beam-top noise relative overflow-hidden px-6 py-14 text-center sm:px-12 sm:py-20">
          <div className="keyline-glow keyline-glow--contained absolute left-0 right-0 top-0" aria-hidden="true" />
          <div className="blob left-[-8%] top-[-30%] h-[300px] w-[300px] bg-[color:rgba(200,154,74,0.4)]" aria-hidden="true" />
          <div className="blob right-[-6%] bottom-[-35%] h-[280px] w-[280px] bg-[color:rgba(185,163,143,0.3)]" style={{ animationDelay: "-8s" }} aria-hidden="true" />
          <div className="relative">
            <span className="eyebrow-light">By Appointment Only</span>
            <h2 className="mx-auto mt-5 max-w-2xl text-3xl font-light tracking-tight text-[color:var(--on-color)] sm:text-4xl lg:text-5xl">
              Ready to glow? Reserve your visit today.
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-[color:rgba(247,241,232,0.72)]">
              Book securely online through our Square booking page. We can’t wait to care for your skin.
            </p>
            <div className="mt-9 flex justify-center">
              <BookButton testId="home-cta-book-appointment-button" variant="bright" className="px-9 py-4" />
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  const videoWrapRef = useRef(null);
  const heroContentRef = useRef(null);
  useHeroScroll(videoWrapRef, heroContentRef);

  return (
    <div>
      <Seo
        title="Bahra Skincare | Luxury Facials & Advanced Skin Treatments in Niles, IL"
        description="Award-winning day spa in Niles, IL offering personalized facials, HydraFacial, dermaplaning, LED light therapy, Derma Pen treatments, chemical exfoliation, brows and tinting. Organic, natural skincare by a licensed specialist. Book online."
        path="/"
        withSchema
      />
      <HeroSection videoWrapRef={videoWrapRef} heroContentRef={heroContentRef} />
      <HighlightsSection />
      <AboutTeaser />
      <ServicesPreview />
      <GalleryPreview />
      {/* REVIEWS */}
      <div className="bg-[color:rgba(217,183,122,0.13)]">
        <ReviewsSection />
      </div>
      <CtaStrip />
    </div>
  );
}
